import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useNavigate } from 'react-router-dom';
import { AlertCircle, ArrowLeft, Play, Square } from 'lucide-react'; 
import { useVendorBooking, useUpdateBookingStatus } from '../../services/vendorBookingService';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { paymentApi } from '../../services/paymentService';
import PaymentHistoryModal from '../../components/client/PaymentHistoryModal';
import StatusUpdateModal from '../../components/vendor/StatusUpdateModal';
import EventStartModal from '../../components/vendor/EventStartModal';
import EventEndModal from '../../components/vendor/EventEndModal';
import EventLogs from '../../components/shared/EventLogs';
import { useEventLogs } from '../../hooks/useEventLogs';
import BookingChatPanel from '../../components/booking/BookingChatPanel';
import { bookingChatUnreadQueryKey, fetchBookingChatUnreadCount } from '../../services/bookingChatService';
import { formatDate, formatTime } from '../../utils/dateUtils';
import {
  BookingHeader,
  BookingReference,
  EventDetails,
  ServicePackageDetails,
  ClientReview,
  ClientInformation,
  PaymentSummary
} from '../../components/vendor/booking';
import EventDateChangeReviewPanel from '../../components/vendor/booking/EventDateChangeReviewPanel';

const VendorBookingDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showPaymentHistoryModal, setShowPaymentHistoryModal] = useState(false);
  const [showStartEventModal, setShowStartEventModal] = useState(false);
  const [showEndEventModal, setShowEndEventModal] = useState(false);
  const [startEventSuccess, setStartEventSuccess] = useState(false);
  const [endEventSuccess, setEndEventSuccess] = useState(false);
  const [showBookingChat, setShowBookingChat] = useState(false);
  
  // Event logs hook
  const { eventLogs, startEvent, endEvent, isStarting, isEnding } = useEventLogs(id);

  // Close start event modal when mutation completes successfully
  useEffect(() => {
    if (startEventSuccess && !isStarting) {
      setShowStartEventModal(false);
      setStartEventSuccess(false);
    }
  }, [startEventSuccess, isStarting]);

  // Close end event modal when mutation completes successfully
  useEffect(() => {
    if (endEventSuccess && !isEnding) {
      setShowEndEventModal(false);
      setEndEventSuccess(false);
    }
  }, [endEventSuccess, isEnding]);

  // Fetch booking details
  const { data: bookingResponse, isLoading, error } = useVendorBooking(id);
  const booking = bookingResponse?.data;

  const closeBookingChat = () => {
    setShowBookingChat(false);
    if (id) {
      queryClient.invalidateQueries({ queryKey: bookingChatUnreadQueryKey(id, false) });
    }
  };

  const { data: chatUnreadResponse } = useQuery({
    queryKey: bookingChatUnreadQueryKey(id, false),
    queryFn: () => fetchBookingChatUnreadCount(id, false),
    enabled: Boolean(id && booking && !showBookingChat),
    refetchInterval: showBookingChat ? false : 12000,
  });
  const chatUnreadCount = chatUnreadResponse?.data?.unread_count ?? 0;

  // Fetch payments for this booking
  const { data: paymentsResponse } = useQuery({
    queryKey: ['payments', id],
    queryFn: () => paymentApi.getPaymentByBooking(id),
    enabled: !!id && !!booking,
    select: (response) => response?.data || []
  });

  const payments = paymentsResponse || [];

  // Update booking status mutation
  const updateStatusMutation = useUpdateBookingStatus({
    onSuccess: () => {
      queryClient.invalidateQueries(['vendorBooking', id]);
      setShowStatusModal(false);
    },
    onError: (error) => {
      console.error('Failed to update booking status:', error);
    }
  });

  const handleStatusUpdate = (newStatus, reason) => {
    if (newStatus && newStatus !== booking?.status) {
      updateStatusMutation.mutate({
        bookingId: id,
        status: newStatus,
        reason: reason || ''
      });
    }
  };

  // Helper function to safely format currency
  const formatCurrency = (value) => {
    // Handle all edge cases
    if (value === null || value === undefined || value === '' || value === 'null' || value === 'undefined') {
      return '0.00';
    }
    // Convert to string and clean
    const strValue = String(value).trim();
    if (strValue === '' || strValue === 'NaN' || strValue === 'null' || strValue === 'undefined') {
      return '0.00';
    }
    // Remove any non-numeric characters except decimal point and minus sign
    const cleaned = strValue.replace(/[^0-9.-]/g, '');
    if (cleaned === '' || cleaned === '-' || cleaned === '.') {
      return '0.00';
    }
    const num = parseFloat(cleaned);
    if (isNaN(num) || !isFinite(num)) {
      return '0.00';
    }
    return num.toFixed(2);
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800',
      confirmed: 'bg-green-100 text-green-800',
      in_progress: 'bg-blue-100 text-blue-800',
      completed: 'bg-green-100 text-green-800',
      cancelled: 'bg-red-100 text-red-800',
      no_show: 'bg-gray-100 text-gray-800'
    };
    return colors[status] || colors.pending;
  };

  const formatStatusText = (status) => {
    return status?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Pending';
  };

  // Generate invoice HTML for download
  const generateInvoiceHTML = () => {
    if (!booking) {
      alert('Booking data not available');
      return '';
    }
    
    // Ensure booking has required fields
    if (!booking.booking_reference) {
      alert('Booking reference not available');
      return '';
    }

    const completedPayments = payments.filter(p => p.status === 'completed');
    
    // Helper function to safely parse numeric values (handles strings from database)
    // Laravel decimal:2 casts return as strings in JSON, so we need robust parsing
    const getNumeric = (val) => {
      // Handle null, undefined, empty
      if (val === null || val === undefined || val === '') return 0;
      
      // Convert to string and trim
      const strVal = String(val).trim();
      if (strVal === '' || strVal === 'null' || strVal === 'undefined' || strVal === 'NaN') {
        return 0;
      }
      
      // Remove any non-numeric characters except decimal point and minus sign
      const cleaned = strVal.replace(/[^0-9.-]/g, '');
      if (cleaned === '' || cleaned === '-' || cleaned === '.') {
        return 0;
      }
      
      // Parse to float
      const parsed = parseFloat(cleaned);
      
      // Validate result
      if (isNaN(parsed) || !isFinite(parsed)) {
        console.warn('getNumeric: Invalid value parsed', { original: val, cleaned, parsed });
        return 0;
      }
      
      return parsed;
    };
    
    // For vendors: calculate total service amount (amount - platform_fee)
    const totalServiceAmount = completedPayments.reduce((sum, p) => {
      const platformFee = getNumeric(p.platform_fee || 0);
      const paymentAmount = getNumeric(p.amount || 0);
      const serviceAmount = paymentAmount - platformFee;
      // Ensure we're adding valid numbers
      if (isNaN(serviceAmount) || !isFinite(serviceAmount)) {
        console.warn('Invalid serviceAmount in payment calculation', { payment: p, platformFee, paymentAmount, serviceAmount });
        return sum;
      }
      return sum + serviceAmount;
    }, 0);
    
    // Final validation of totalServiceAmount
    const finalTotalServiceAmount = (isNaN(totalServiceAmount) || !isFinite(totalServiceAmount)) ? 0 : totalServiceAmount;
    
    const client = booking?.client;
    const service = booking?.vendor_service;
    const packageData = booking?.vendor_service_package;
    
    // Safely get amounts with proper parsing and validation
    // Values come from database as strings (Laravel decimal:2 casts), so we need to parse them properly
    // Use vendor_payout_amount as primary source (what vendor receives)
    // Debug: Log the raw values to see what we're getting
    console.log('Invoice Generation - Raw Booking Data:', {
      'booking.vendor_payout_amount': booking.vendor_payout_amount,
      'booking.subtotal_amount': booking.subtotal_amount,
      'booking.platform_fee': booking.platform_fee,
      'vendor_payout_type': typeof booking.vendor_payout_amount,
      'subtotal_type': typeof booking.subtotal_amount
    });
    
    const vendorPayoutRaw = booking.vendor_payout_amount ?? booking.subtotal_amount ?? 0;
    const subtotalRaw = booking.subtotal_amount ?? booking.vendor_payout_amount ?? 0;
    
    const vendorPayout = getNumeric(vendorPayoutRaw);
    const subtotal = getNumeric(subtotalRaw);
    
    // Ensure we have valid numbers (fallback to 0 if somehow invalid)
    const finalVendorPayout = (isNaN(vendorPayout) || !isFinite(vendorPayout)) ? 0 : vendorPayout;
    const finalSubtotal = (isNaN(subtotal) || !isFinite(subtotal)) ? finalVendorPayout : subtotal;
    
    // Final validation - ensure we never pass NaN to formatCurrency
    if (isNaN(finalVendorPayout) || isNaN(finalSubtotal)) {
      console.error('Invoice Generation: NaN detected after parsing!', {
        vendorPayoutRaw,
        subtotalRaw,
        vendorPayout,
        subtotal,
        finalVendorPayout,
        finalSubtotal
      });
    }
    
    // Helper functions for invoice template
    const formatDateForInvoice = (date) => formatDate(date, 'long');
    const formatCurrencyForInvoice = (value) => {
      // Double-check we're not passing NaN or invalid values
      if (value === null || value === undefined || value === '') {
        return '0.00';
      }
      // If it's already a number, validate it
      if (typeof value === 'number') {
        if (isNaN(value) || !isFinite(value)) {
          console.error('formatCurrencyForInvoice: Received NaN or invalid number', value);
          return '0.00';
        }
        return formatCurrency(value);
      }
      // If it's a string or other type, parse it first
      const numValue = getNumeric(value);
      if (isNaN(numValue) || !isFinite(numValue)) {
        console.error('formatCurrencyForInvoice: Parsed to NaN', { original: value, parsed: numValue });
        return '0.00';
      }
      return formatCurrency(numValue);
    };
    
    return `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Invoice - ${booking.booking_reference}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 40px; max-width: 800px; margin: 0 auto; }
          .header { text-align: center; margin-bottom: 30px; border-bottom: 2px solid #000; padding-bottom: 20px; }
          .invoice-title { font-size: 24px; font-weight: bold; margin-bottom: 10px; }
          .booking-ref { font-size: 14px; color: #666; }
          .section { margin-bottom: 25px; }
          .section-title { font-size: 18px; font-weight: bold; margin-bottom: 15px; border-bottom: 1px solid #ddd; padding-bottom: 5px; }
          .row { display: flex; justify-content: space-between; margin-bottom: 8px; }
          .label { font-weight: 600; }
          .value { text-align: right; }
          .payment-item { margin-bottom: 10px; padding: 10px; background: #f5f5f5; border-radius: 4px; }
          .total { font-size: 18px; font-weight: bold; margin-top: 20px; padding-top: 15px; border-top: 2px solid #000; }
          .footer { margin-top: 40px; text-align: center; font-size: 12px; color: #666; }
          @media print { body { padding: 20px; } }
        </style>
      </head>
      <body>
        <div class="header">
          <div class="invoice-title">INVOICE</div>
          <div class="booking-ref">Booking #${booking.booking_reference}</div>
          <div style="margin-top: 10px; font-size: 12px;">Date: ${formatDateForInvoice(booking.created_at)}</div>
        </div>

        <div class="section">
          <div class="section-title">Client Information</div>
          <div class="row"><span class="label">Name:</span><span class="value">${client?.name || 'N/A'}</span></div>
          <div class="row"><span class="label">Phone:</span><span class="value">${client?.phone || 'N/A'}</span></div>
          <div class="row"><span class="label">Email:</span><span class="value">${client?.email || 'N/A'}</span></div>
        </div>

        <div class="section">
          <div class="section-title">Booking Details</div>
          <div class="row"><span class="label">Event Date:</span><span class="value">${formatDateForInvoice(booking.event_date)}</span></div>
          <div class="row"><span class="label">Service:</span><span class="value">${service?.service_name || 'N/A'}</span></div>
          ${packageData ? `<div class="row"><span class="label">Package:</span><span class="value">${packageData.package_name}</span></div>` : ''}
        </div>

        <div class="section">
          <div class="section-title">Payment Summary</div>
          <div class="row"><span class="label">Subtotal:</span><span class="value">$${formatCurrencyForInvoice(finalSubtotal)}</span></div>
          <div class="row"><span class="label">Vendor Payout:</span><span class="value">$${formatCurrencyForInvoice(finalVendorPayout)}</span></div>
        </div>

        <div class="section">
          <div class="section-title">Payment Transactions</div>
          ${completedPayments.length > 0 ? completedPayments.map(payment => {
            // Calculate service amount (what vendor receives) = payment amount - platform fee
            const platformFee = getNumeric(payment.platform_fee || 0);
            const paymentAmount = getNumeric(payment.amount || 0);
            const serviceAmount = Math.max(0, paymentAmount - platformFee); // Ensure non-negative
            
            return `
            <div class="payment-item">
              <div class="row"><span class="label">Payment Reference:</span><span class="value">${payment.payment_reference}</span></div>
              <div class="row"><span class="label">Type:</span><span class="value">${payment.payment_type === 'deposit' ? 'Deposit' : 'Full Payment'}</span></div>
              <div class="row"><span class="label">Service Amount:</span><span class="value">$${formatCurrencyForInvoice(serviceAmount)}</span></div>
              <div class="row"><span class="label">Date:</span><span class="value">${formatDateForInvoice(payment.created_at)}</span></div>
            </div>
          `;
          }).join('') : '<div class="payment-item">No payments recorded</div>'}
        </div>

        <div class="total">
          <div class="row">
            <span class="label">Total Service Amount:</span>
            <span class="value">$${formatCurrencyForInvoice(finalTotalServiceAmount)}</span>
          </div>
        </div>

        <div class="footer">
          <p>This is an official invoice for your records.</p>
        </div>
      </body>
      </html>
    `;
  };

  const handleDownloadInvoice = () => {
    try {
      const invoiceHTML = generateInvoiceHTML();
      if (!invoiceHTML || invoiceHTML === '') {
        return; // Error already handled in generateInvoiceHTML
      }
      const printWindow = window.open('', '_blank');
      if (!printWindow) {
        alert('Please allow pop-ups to download the invoice');
        return;
      }
      printWindow.document.write(invoiceHTML);
      printWindow.document.close();
      setTimeout(() => {
        printWindow.print();
      }, 250);
    } catch (error) {
      console.error('Error generating invoice:', error);
      alert('Failed to generate invoice. Please try again later.');
    }
  };

  // Skeleton Loading Component
  const SkeletonLoader = () => (
    <div className="min-h-screen bg-gray-50 p-3 sm:p-6">
      <div className="space-y-3 sm:space-y-2 animate-pulse">
        <div className="h-5 w-32 bg-gray-200 rounded"></div>
        <div className="bg-white border border-black rounded-lg p-2 sm:p-3">
          <div className="h-6 w-48 bg-gray-200 rounded mb-2"></div>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-2 sm:gap-3">
          <div className="lg:col-span-2 space-y-2 sm:space-y-3">
            <div className="bg-white border border-black rounded-lg p-2 sm:p-3">
              <div className="h-5 w-32 bg-gray-200 rounded mb-3"></div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
                {[1, 2, 3, 4].map((i) => (
                  <div key={i} className="h-16 bg-gray-200 rounded"></div>
                ))}
              </div>
            </div>
          </div>
          <div className="space-y-2 sm:space-y-3">
            <div className="bg-white border border-black rounded-lg p-2 sm:p-3">
              <div className="h-5 w-40 bg-gray-200 rounded mb-3"></div>
              <div className="space-y-2">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="h-4 w-full bg-gray-200 rounded"></div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
      </div>
    );

  if (isLoading) {
    return <SkeletonLoader />;
  }

  if (error || !booking) {
    return (
      <div className="min-h-screen bg-gray-50 p-3 sm:p-6">
        <div className="bg-white border border-black rounded-lg p-6 sm:p-8">
          <div className="text-center">
            <AlertCircle className="w-12 h-12 text-black mx-auto mb-4" />
            <h3 className="text-xl font-bold text-black mb-2">Booking Not Found</h3>
            <p className="text-gray-600 mb-6">
              {error?.response?.data?.message || 'The booking you are looking for does not exist.'}
            </p>
            <button
              onClick={() => navigate('/vendor/bookings')}
              className="inline-flex items-center px-4 py-2 bg-black text-white rounded-md hover:bg-gray-900 transition-colors"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Bookings
            </button>
          </div>
        </div>
      </div>
    );
  }

  const client = booking.client;
  const service = booking.vendor_service;
  const packageData = booking.vendor_service_package;

  // Calculate payment totals
  const completedPayments = payments.filter(p => p.status === 'completed');
  // For vendors: calculate service amount (amount - platform_fee), not total paid
  const totalServiceAmount = completedPayments.reduce((sum, p) => {
    const platformFee = parseFloat(p.platform_fee || 0);
    const paymentAmount = parseFloat(p.amount || 0);
    return sum + (paymentAmount - platformFee);
  }, 0);
  const depositPaid = completedPayments.filter(p => p.payment_type === 'deposit')
    .reduce((sum, p) => {
      const platformFee = parseFloat(p.platform_fee || 0);
      const paymentAmount = parseFloat(p.amount || 0);
      return sum + (paymentAmount - platformFee);
    }, 0);
  const fullPaymentPaid = completedPayments.filter(p => p.payment_type === 'full_payment').length > 0;

  // Check for reviews
  const hasReview = booking.reviews && booking.reviews.length > 0;
  const review = hasReview ? booking.reviews[0] : null;

  return (
    <div className="min-h-screen bg-gray-50 p-3 sm:p-6">
      <div className="space-y-3 sm:space-y-2">
        {/* Header */}
        <BookingHeader
          onBack={() => navigate('/vendor/bookings')}
          status={booking.status}
          statusColor={getStatusColor(booking.status)}
          statusText={formatStatusText(booking.status)}
          onUpdateStatus={() => {
            // Prevent opening modal if status is already completed
            if (booking.status !== 'completed') {
              setShowStatusModal(true);
            }
          }}
        />

        {/* Booking Reference */}
        <BookingReference
          bookingReference={booking.booking_reference}
          createdAt={booking.created_at}
          formatDate={formatDate}
        />

        {/* Event Start/End Actions */}
        {booking.status !== 'cancelled' && (
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
              <div className="min-w-0 flex-1">
                <h3 className="text-sm font-bold text-gray-900 mb-1">Event Management</h3>
                <p className="text-xs text-gray-600 max-w-2xl">
                  Start and end the event on the event date to track progress. If you don&apos;t press
                  Start by the scheduled start time, or End after the scheduled end time, the system can
                  record automatic start/end logs (scheduler — refresh to see updates).
                </p>
              </div>
              <div className="flex shrink-0 items-center gap-3 self-start">
                {!eventLogs.find(log => log.event_type === 'start') && (() => {
                  // Check if event date has arrived
                  const today = new Date();
                  today.setHours(0, 0, 0, 0);
                  
                  const eventDate = booking?.event_date ? new Date(booking.event_date) : null;
                  if (eventDate) {
                    eventDate.setHours(0, 0, 0, 0);
                  }
                  
                  const isEventDateReached = eventDate ? eventDate <= today : false;
                  const isDisabled = booking.status === 'completed' || 
                                   booking.status === 'cancelled' || 
                                   !isEventDateReached;
                  
                  return (
                    <button
                      onClick={() => setShowStartEventModal(true)}
                      disabled={isDisabled}
                      className="flex shrink-0 items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium whitespace-nowrap"
                      title={!isEventDateReached ? 'Event date has not arrived yet' : ''}
                    >
                      <Play className="w-4 h-4" />
                      Start Event
                    </button>
                  );
                })()}
                {eventLogs.find(log => log.event_type === 'start') && !eventLogs.find(log => log.event_type === 'end') && (
                  <button
                    onClick={() => setShowEndEventModal(true)}
                    disabled={booking.status === 'completed' || booking.status === 'cancelled'}
                    className="flex shrink-0 items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium whitespace-nowrap"
                  >
                    <Square className="w-4 h-4" />
                    End Event
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Event Logs */}
        {eventLogs && eventLogs.length > 0 && (
          <EventLogs eventLogs={eventLogs} isLoading={false} />
        )}

        {(booking.active_event_date_change_request ||
          booking.activeEventDateChangeRequest) && (
          <EventDateChangeReviewPanel
            bookingId={booking.id}
            changeRequest={
              booking.active_event_date_change_request ?? booking.activeEventDateChangeRequest
            }
            clientName={client?.name}
            onResolved={() => {
              queryClient.invalidateQueries({ queryKey: ['vendorBooking', id] });
            }}
          />
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-2 sm:gap-3">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-2 sm:space-y-3">
            {/* Event Details */}
            <EventDetails
              booking={booking}
              formatDate={formatDate}
              formatTime={formatTime}
            />

            {/* Service & Package Details */}
            <ServicePackageDetails
              service={service}
              packageData={packageData}
            />

            {/* Review Section */}
            <ClientReview review={review} />
          </div>

          {/* Sidebar */}
          <div className="space-y-2 sm:space-y-3">
            {/* Client Information */}
            <ClientInformation
              client={client}
              onMessageClient={() => setShowBookingChat(true)}
              unreadChatCount={chatUnreadCount}
            />

            {/* Payment Summary */}
            <PaymentSummary
              booking={booking}
              totalPaid={totalServiceAmount}
              depositPaid={depositPaid}
              fullPaymentPaid={fullPaymentPaid}
              payments={payments}
              onDownloadInvoice={handleDownloadInvoice}
              onViewPaymentHistory={() => setShowPaymentHistoryModal(true)}
            />
          </div>
        </div>
      </div>

      {/* Status Update Modal */}
      <StatusUpdateModal
        isOpen={showStatusModal}
        onClose={() => setShowStatusModal(false)}
        currentStatus={booking?.status}
        onUpdate={handleStatusUpdate}
        isUpdating={updateStatusMutation.isPending}
      />

      {/* Payment History Modal */}
      <PaymentHistoryModal
        isOpen={showPaymentHistoryModal}
        onClose={() => setShowPaymentHistoryModal(false)}
        payments={payments}
        isVendorView={true}
      />

      {/* Event Start Modal */}
      <EventStartModal
        isOpen={showStartEventModal}
        onClose={() => {
          setShowStartEventModal(false);
        }}
        onStart={(data) => {
          startEvent(data, {
            onSuccess: () => {
              setStartEventSuccess(true);
            },
          });
        }}
        isLoading={isStarting}
      />

      {/* Event End Modal */}
      <EventEndModal
        isOpen={showEndEventModal}
        onClose={() => setShowEndEventModal(false)}
        onEnd={(data) => {
          endEvent(data, {
            onSuccess: () => {
              setEndEventSuccess(true);
            },
          });
        }}
        isLoading={isEnding}
      />

      {showBookingChat &&
        id &&
        createPortal(
          <div
            className="fixed inset-0 z-[10050] flex items-end sm:items-center justify-center p-4 bg-black/50"
            role="dialog"
            aria-modal="true"
            onClick={(e) => {
              if (e.target === e.currentTarget) closeBookingChat();
            }}
          >
            <div className="w-full max-w-lg max-h-[90vh] flex flex-col">
              <BookingChatPanel bookingId={id} onClose={closeBookingChat} className="max-h-[90vh]" />
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};

export default VendorBookingDetails;
